
import { useState, useEffect } from "react";
import { Calendar, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BlogPost } from "@/types/blogPost";
import { extractVideoFromContent } from "@/utils/videoContent";
import { BlogContent } from "./BlogContent";
import { RelatedPostsCarousel } from "./RelatedPostsCarousel";
import { fetchRelatedBlogPosts } from "@/utils/blogOperations";

// ── Video helpers ──────────────────────────────────────────────────────────────
function getYouTubeEmbedUrl(url: string): string | null {
  const patterns = [
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
  }
  return null;
}

function isExternalOnly(url: string) {
  return /instagram\.com|tiktok\.com|facebook\.com|fb\.watch/.test(url);
}

function getExternalLabel(url: string) {
  if (/instagram\.com/.test(url)) return "Instagram";
  if (/tiktok\.com/.test(url)) return "TikTok";
  return "Facebook";
}

function PostVideo({ url }: { url: string }) {
  const ytEmbed = getYouTubeEmbedUrl(url);
  if (ytEmbed) {
    return (
      <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
        <iframe src={ytEmbed} className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen />
      </div>
    );
  }
  if (isExternalOnly(url)) {
    const label = getExternalLabel(url);
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-4 bg-brand-primary rounded-2xl px-6 py-5 text-white hover:opacity-90 transition-opacity">
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center shrink-0">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
        <div>
          <p className="font-bold font-primary">Assistir no {label}</p>
          <p className="text-white/70 text-sm">Clique para abrir o vídeo</p>
        </div>
      </a>
    );
  }
  return (
    <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
      <video src={url} controls className="w-full h-full object-cover" />
    </div>
  );
}

interface BlogPostDisplayProps {
  post: BlogPost;
  onBack: () => void;
}

export function BlogPostDisplay({ post, onBack }: BlogPostDisplayProps) {
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    const loadRelatedPosts = async () => {
      if (post.category_id) {
        try {
          const related = await fetchRelatedBlogPosts(post.id, post.category_id);
          setRelatedPosts(related);
        } catch (error) {
          console.error('Error loading related posts:', error);
        }
      }
    };

    loadRelatedPosts();
  }, [post.id, post.category_id]);

  const categoryInfo = (post as any).blog_categories;
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-8">
      <Button onClick={onBack} variant="outline" className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar aos Artigos
      </Button>

      <article className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>
          {post.subtitle && (
            <h2 className="text-xl sm:text-2xl text-gray-600 mb-6">
              {post.subtitle}
            </h2>
          )}
          
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm text-gray-600">
                {post.created_at ? formatDate(post.created_at) : "Data não informada"}
              </span>
            </div>
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm text-gray-600">Dra Brenda Ramos</span>
            </div>
            {categoryInfo && (
              <Badge 
                style={{ 
                  backgroundColor: categoryInfo.color_hex,
                  borderColor: categoryInfo.border_color_hex,
                  color: '#000'
                }}
                className="border"
              >
                {categoryInfo.name}
              </Badge>
            )}
          </div>
        </header>

        {/* Featured Image */}
        {post.featured_image_url && (
          <div className="mb-8">
            <img
              src={post.featured_image_url}
              alt={post.title}
              className="w-full aspect-video object-cover rounded-lg"
            />
          </div>
        )}

        {/* Content + Video extracted from content string */}
        {(() => {
          const { clean, videoUrl } = extractVideoFromContent(post.content || "");
          return (
            <>
              <div className="prose prose-lg max-w-none">
                <BlogContent content={clean} />
              </div>
              {videoUrl && (
                <div className="mt-10">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Vídeo</p>
                  <PostVideo url={videoUrl} />
                </div>
              )}
            </>
          );
        })()}
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <RelatedPostsCarousel posts={relatedPosts} />
      )}
    </div>
  );
}
